import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck2, X } from 'lucide-react';
import { fechaCorta, horaCR, plural } from 'lib/auditoria';
import { rutaInspeccion } from 'lib/auditoriaInspeccion';
import {
  ESTADO_CELDA_LABEL, esCierreTecnico, esDeclarada, estadoCelda, hallazgosPorItem, mesesCalendario,
} from 'lib/reporteControl';
import EstadoPill, { MarcaTardio } from 'components/EstadoPill';
import Button from 'components/ui/Button';
import Bloque, { Cifras } from './Bloque';
import Calendario, { CELDA, Leyenda } from './Calendario';

export const RECIENTES = 5;

export function textoDiario(r) {
  if (!r.dias) return 'El período todavía no tiene días.';
  const partes = [`${plural(r.conInspeccion, 'día con inspección', 'días con inspección')} de ${r.dias}`];
  if (r.cumplimiento !== null) partes.push(`${String(r.cumplimiento).replace('.', ',')} % de los días que se operó`);
  return `${partes.join(', ')}.`;
}

function DetalleDia({ dia, estado, puedeMarcar, esAdmin, onMarcar, onQuitar, onCerrar }) {
  const marca = dia.sinOperacion;
  return (
    <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3 text-sm" data-testid={`dia-${dia.fecha}`} aria-live="polite">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`h-3 w-3 shrink-0 rounded-[3px] ${CELDA[estado]}`} aria-hidden="true" />
        <span className="font-semibold tabular-nums text-foreground">{fechaCorta(dia.fecha)}</span>
        <span className="text-muted-foreground">{ESTADO_CELDA_LABEL[estado]}</span>
        {dia.resultado && dia.resultado !== 'aprobado' && <EstadoPill estado={dia.resultado} />}
        {dia.tardio && <MarcaTardio />}
        <button type="button" onClick={onCerrar} aria-label="Cerrar el detalle del día"
          className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      {dia.inspecciones.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {dia.inspecciones.map((r) => (
            <li key={r.id}>
              <Link to={rutaInspeccion(r.id)} className="text-primary underline-offset-2 hover:underline">
                {r.guia_nombre || 'Sin nombre'} ({horaCR(r.created_at) || 'sin hora'})
              </Link>
              {esDeclarada(r) && <span className="ml-1.5 text-xs text-muted-foreground">declarada, registrada después</span>}
            </li>
          ))}
        </ul>
      )}
      {marca && <p className="mt-1.5 text-muted-foreground">Motivo: {marca.motivo}</p>}
      {dia.anuladas.map((a) => (
        <p key={a.id} className="mt-1 text-xs text-muted-foreground">
          <Link to={rutaInspeccion(a.id)} className="line-through underline-offset-2 hover:underline">{a.guia_nombre || 'Sin nombre'}</Link> anulada: {a.anulada_motivo || 'sin motivo'}
        </p>
      ))}
      {(dia.marcasAnuladas || []).map((m) => (
        <p key={m.id} className="mt-1 text-xs text-muted-foreground" data-testid="marca-anulada">
          Se quitó la marca «No se operó» (<span className="line-through">{m.motivo}</span>): {m.anulada_motivo || 'sin motivo'}
        </p>
      ))}
      {puedeMarcar && (dia.estado === 'sin_registro' || dia.estado === 'pendiente') && (
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => onMarcar(dia)}>
          No se operó
        </Button>
      )}
      {/* El cierre técnico no se quita. Otra marca, solo admin, por si se puso por error. */}
      {esAdmin && marca && !esCierreTecnico(marca) && (
        <Button type="button" variant="outline" size="sm" className="mt-2 text-error hover:bg-error hover:text-error-foreground"
          onClick={() => onQuitar(dia)}>
          Quitar marca
        </Button>
      )}
    </div>
  );
}

export default function BloqueDiario({
  dias, resumen, hallazgos, periodo, instalacion, puedeMarcar, esAdmin, onMarcar, onQuitar,
}) {
  const [seleccion, setSeleccion] = useState(null);
  const meses = useMemo(() => mesesCalendario(dias, periodo), [dias, periodo]);
  const porItem = useMemo(() => hallazgosPorItem(hallazgos), [hallazgos]);
  const conteo = useMemo(() => {
    const c = {};
    for (const m of meses) for (const x of m.celdas) if (x && x.estado !== 'fuera') c[x.estado] = (c[x.estado] || 0) + 1;
    return c;
  }, [meses]);
  const dia = seleccion ? dias.find((d) => d.fecha === seleccion) : null;
  const qs = new URLSearchParams({ desde: periodo.desde, hasta: periodo.hasta });
  if (instalacion !== 'canopy') qs.set('instalacion', instalacion);

  return (
    <Bloque titulo="Inspección diaria" icono={CalendarCheck2} testId="bloque-diario"
      resumen={<span data-testid="resumen-periodo">{textoDiario(resumen)}</span>}
      cifras={(
        <Cifras items={[
          { label: 'Días del período', valor: resumen.dias },
          { label: 'Con inspección', valor: resumen.conInspeccion },
          { label: 'Declaradas', valor: resumen.declaradas },
          { label: 'No se operó', valor: resumen.sinOperacion },
          { label: 'Sin inspección', valor: resumen.sinRegistro, tono: resumen.sinRegistro ? 'alerta' : '' },
          { label: 'Con alerta', valor: resumen.conAlerta, tono: resumen.conAlerta ? 'alerta' : '' },
          { label: 'Con falla', valor: resumen.conFalla, tono: resumen.conFalla ? 'falla' : '' },
        ]} />
      )}>
      <h3 className="mb-2 text-sm font-semibold text-foreground">Calendario del período</h3>
      <Calendario meses={meses} seleccion={seleccion} onElegir={setSeleccion} />
      <div className="mt-3"><Leyenda conteo={conteo} /></div>
      {dia ? (
        <DetalleDia dia={dia} estado={estadoCelda(dia)}
          puedeMarcar={puedeMarcar} esAdmin={esAdmin} onMarcar={onMarcar} onQuitar={onQuitar} onCerrar={() => setSeleccion(null)} />
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">
          Toque un día para ver sus inspecciones{puedeMarcar ? ' o marcar que no se operó' : ''}.
          {resumen.declaradas > 0 && ` Las declaradas son inspecciones reportadas en el grupo de Operaciones y registradas después.`}
          {resumen.pendienteHoy > 0 && ' Hoy todavía no tiene inspección y no cuenta como día sin inspección.'}
        </p>
      )}

      <div className="mt-5 border-t border-border pt-3">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground">
            Alertas y fallas <span className="font-normal text-muted-foreground">({plural(hallazgos.length, 'ítem', 'ítems')})</span>
          </h3>
          {hallazgos.length > 0 && (
            <Link to={`/hallazgos?${qs}`} className="text-sm font-semibold text-primary underline-offset-2 hover:underline">
              Ver todas
            </Link>
          )}
        </div>
        {hallazgos.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin alertas ni fallas en el período.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="mb-1 text-xs font-medium text-muted-foreground">Por ítem del checklist</h4>
              <ul className="divide-y divide-border text-sm" data-testid="hallazgos-por-item">
                {porItem.map((x) => (
                  <li key={x.itemKey} className="flex items-center gap-2 py-1.5">
                    <span className="min-w-0 flex-1 truncate text-foreground">{x.item}</span>
                    {x.alertas > 0 && <span className="whitespace-nowrap text-xs tabular-nums text-warning-foreground">{plural(x.alertas, 'alerta', 'alertas')}</span>}
                    {x.fallas > 0 && <span className="whitespace-nowrap text-xs font-semibold tabular-nums text-error">{plural(x.fallas, 'falla', 'fallas')}</span>}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="mb-1 text-xs font-medium text-muted-foreground">Más recientes</h4>
              <ul className="divide-y divide-border text-sm">
                {hallazgos.slice(0, RECIENTES).map((h) => (
                  <li key={`${h.inspeccion_id}:${h.itemKey}`} data-testid="hallazgo" className="py-1.5">
                    <Link to={rutaInspeccion(h.inspeccion_id)} className="group flex items-center gap-2">
                      <EstadoPill estado={h.estado} />
                      <span className="min-w-0 flex-1 truncate text-foreground group-hover:underline">{h.item}</span>
                      <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{fechaCorta(h.fecha)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </Bloque>
  );
}
